import {
  FC,
  DragEvent as ReactDragEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  MeasuringStrategy,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import {
  IconSizes,
  MagnifyerLineIcon,
  MultiplyIcon,
  PlusCircleFullIcon
} from '@inno-ui/icons'
import cn from 'classnames'
import { isAiArtifactTile } from 'modules/desktop/modules/widgets/components/AiArtifacts/utils/aiTilesPersistence'
import { WidgetWrapperProps } from 'modules/desktop/modules/widgets/components/WidgetWrapper/WidgetWrapper.types'
import { WidgetsErrorBoundary } from 'modules/desktop/modules/widgets/components/WidgetsErrorBoundary'
import {
  DEFAULT_WIDGET_SIZE,
  WIDGET_COLLAPSED_HEIGHT,
  WIDGET_NATURAL_HEIGHTS
} from 'modules/desktop/modules/widgets/constants/widgetDimensions'
import { WIDGETS_CONFIG } from 'modules/desktop/modules/widgets/constants/widgetsConfig'
import { useWidgetMutations } from 'modules/desktop/modules/widgets/hooks'
import {
  normalizeDisplayOrder,
  readWidgetPayload
} from 'modules/desktop/modules/widgets/utils'
import type { DroppedWidgetSpec } from 'modules/desktop/modules/widgets/utils'
import { getDragContext } from 'modules/desktop/pages/MainPage/stores/dragStore'
import { MarketplaceDragPayload } from 'modules/desktop/pages/MainPage/stores/dragStore'
import { motion } from 'motion/react'
import { AnimatePresence } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { useGetArtifactsQuery } from 'shared/api/forgeApi'
import { WidgetSize } from 'shared/constants/widgets'
import { useDebounce } from 'shared/hooks/useDebounce'
import { useUserContext } from 'shared/hooks/useUserContext'
import { logger } from 'shared/services/logger'
import { useDesktopUtilityWindowsStore } from 'shared/stores/desktopUtilityWindowsStore'
import { widgetSingletonKey } from 'shared/utils/dedupeWidgets'
import { generateWidgetId } from 'shared/utils/generateWidgetId'

import { useOpenWindowMarketplace } from '../../hooks/useOpenWindowMarketplace'
import { useRemoveWidget } from '../../hooks/useRemoveWidget'
import { useSidebarWidgetsStore } from '../../stores'
import { useRefsStore } from '../../stores/refsStore'
import {
  TOUCH_DRAG_DELAY_MS,
  TOUCH_DRAG_TOLERANCE
} from '../../utils/touchDrag.constants'
import { useTouchDropZone } from '../../utils/useTouchDropZone'
import { useTouchEnvironment } from '../../utils/useTouchEnvironment'
import { LazyWidgetMount } from './LazyWidgetMount'
import styles from './SidebarWidgets.module.scss'
import { SidebarWidgetsProps } from './SidebarWidgets.types'

const MARKETPLACE_CONTEXT_FALLBACK = DEFAULT_WIDGET_SIZE
const SEARCH_DEBOUNCE_MS = 300

type DropPreviewState = {
  index: number
} & DroppedWidgetSpec

export const SidebarWidgets: FC<SidebarWidgetsProps> = ({
  showDropBackground,
  showRemoveWidgetButtons
}) => {
  const { user } = useUserContext()
  const { t } = useTranslation('translation', { keyPrefix: 'widgets' })

  const { items, canReorder, editWidgets, updateWidget } = useWidgetMutations()
  const { data: artifacts } = useGetArtifactsQuery()
  const artifactTitles = useMemo(
    () =>
      new Map(
        (artifacts ?? []).map(
          artifact => [artifact.public_id, artifact.title] as const
        )
      ),
    [artifacts]
  )

  // The backend materializes forced widgets and marks each persisted row.
  // They remain pinned to the top, undeletable, and outside sortable DnD.
  const displayItems = useMemo(
    () =>
      items
        .filter(item => !WIDGETS_CONFIG[item.widgetKey]?.isHidden?.(user))
        .map(item => {
          const sizes = WIDGETS_CONFIG[item.widgetKey]?.sizes
          return sizes?.length && !sizes.includes(item.size)
            ? { ...item, size: sizes[0] }
            : item
        }),
    [items, user]
  )
  const forcedWidgets = useMemo(
    () => displayItems.filter(item => item.isForced),
    [displayItems]
  )
  const sortableItems = useMemo(
    () => displayItems.filter(item => !item.isForced),
    [displayItems]
  )

  const fullViewWidgetId = useSidebarWidgetsStore(
    state => state.fullViewWidgetId
  )
  const toggleFullView = useSidebarWidgetsStore(state => state.toggleFullView)
  const setFullViewWidgetId = useSidebarWidgetsStore(
    state => state.setFullViewWidgetId
  )
  const removeWidget = useRemoveWidget()

  const [dropPreview, setDropPreview] = useState<DropPreviewState | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const searchQuery = useDebounce(
    searchInput.trim().toLocaleLowerCase(),
    SEARCH_DEBOUNCE_MS
  )
  const previousSearchQuery = useRef(searchQuery)
  const suppressWidgetMotion = Boolean(
    searchInput.trim() ||
      searchQuery ||
      previousSearchQuery.current !== searchQuery
  )
  useEffect(() => {
    previousSearchQuery.current = searchQuery
  }, [searchQuery])
  const dropIndexRef = useRef<number | null>(null)
  const isDesktopUtilityWindowOpen = useDesktopUtilityWindowsStore(
    state => state.activeWindow !== null
  )

  const dropZoneRef = useRef<HTMLDivElement>(null)
  const savedScrollPosition = useRef<number>(0)
  const { setSidebarWidgetsRef } = useRefsStore()

  const [activeId, setActiveId] = useState<string | null>(null)
  const [isDropHovered, setIsDropHovered] = useState(false)
  const lastDragOverId = useRef<string | null>(null)
  const isTouchEnvironment = useTouchEnvironment()

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 4 }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: TOUCH_DRAG_DELAY_MS,
        tolerance: TOUCH_DRAG_TOLERANCE
      }
    })
  )

  // widgets whose catalog entry is hidden for this env with hiddenAction:
  // 'remove' (e.g. a dev-only widget in prod) never render — they're
  // auto-removed as soon as they'd otherwise appear.
  useLayoutEffect(() => {
    items.forEach(item => {
      const config = WIDGETS_CONFIG[item.widgetKey]
      if (config?.isHidden?.(user) && config?.hiddenAction === 'remove') {
        removeWidget(item.id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, user])

  const dragEnterCounter = useRef(0)

  const getIndexForPosition = useCallback(
    (clientY: number, container: HTMLElement) => {
      const widgetEls = Array.from(
        container.querySelectorAll<HTMLElement>('[data-sidebar-widget="true"]')
      )
      for (let i = 0; i < widgetEls.length; i += 1) {
        const rect = widgetEls[i].getBoundingClientRect()
        if (clientY < rect.top + rect.height / 2) {
          const raw = Number(widgetEls[i].dataset.sidebarWidgetIndex)
          return Number.isFinite(raw) ? raw : i
        }
      }
      return sortableItems.length
    },
    [sortableItems.length]
  )

  const updateDropPreview = useCallback(
    (
      payload: DroppedWidgetSpec | null,
      clientY: number,
      container: HTMLElement
    ) => {
      if (!payload) {
        dropIndexRef.current = null
        setDropPreview(null)
        setIsDropHovered(false)
        return
      }

      setIsDropHovered(true)
      const index = canReorder
        ? getIndexForPosition(clientY, container)
        : sortableItems.length
      dropIndexRef.current = index
      setDropPreview({
        index,
        size: payload.size,
        widgetKey: payload.widgetKey,
        ...(payload.config ? { config: payload.config } : {})
      })
    },
    [canReorder, getIndexForPosition, sortableItems.length]
  )

  const insertItem = useCallback(
    (index: number, payload: DroppedWidgetSpec) => {
      editWidgets((current, canReorderNow) => {
        const singletonKey = widgetSingletonKey(payload)
        if (current.some(item => widgetSingletonKey(item) === singletonKey)) {
          return null
        }
        const forced = current.filter(item => item.isForced)
        const sortable = current.filter(item => !item.isForced)
        const insertionIndex = canReorderNow
          ? Math.max(0, Math.min(index, sortable.length))
          : sortable.length
        const newWidget = {
          id: generateWidgetId(),
          widgetKey: payload.widgetKey,
          size: payload.size,
          displayOrder: current.length + 1,
          isCollapsed: false,
          ...(payload.config ? { config: payload.config } : {})
        }
        const nextSortable = [...sortable]
        nextSortable.splice(insertionIndex, 0, newWidget)
        const nextWidgets = normalizeDisplayOrder([...forced, ...nextSortable])
        return canReorderNow
          ? { nextWidgets }
          : { nextWidgets, widgetsToSave: [newWidget] }
      })
    },
    [editWidgets]
  )

  const tryResolvePayload = useCallback(
    (event: ReactDragEvent<HTMLDivElement>): DroppedWidgetSpec | null => {
      const payload = readWidgetPayload(event)
      if (payload) return payload

      const ctx = getDragContext()
      if (ctx?.source === 'marketplace') {
        return {
          widgetKey: ctx.widgetKey,
          size: ctx.size ?? MARKETPLACE_CONTEXT_FALLBACK,
          ...(ctx.config ? { config: ctx.config } : {})
        }
      }

      return null
    },
    []
  )

  const handleHtmlDragEnter = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault()
      dragEnterCounter.current += 1
    },
    []
  )

  const handleHtmlDragOver = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault()
      const payload = tryResolvePayload(event)
      updateDropPreview(payload, event.clientY, event.currentTarget)
    },
    [tryResolvePayload, updateDropPreview]
  )

  const handleHtmlDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault()
      dragEnterCounter.current = 0
      const payload = tryResolvePayload(event)
      if (!payload) {
        dropIndexRef.current = null
        setDropPreview(null)
        setIsDropHovered(false)
        return
      }

      // The preview has already moved widgets in the DOM. Re-measuring here
      // can turn the intended insertion point into the end of the list.
      const index =
        dropIndexRef.current ??
        (canReorder
          ? getIndexForPosition(event.clientY, event.currentTarget)
          : sortableItems.length)
      insertItem(index, payload)
      dropIndexRef.current = null
      setDropPreview(null)
      setIsDropHovered(false)
    },
    [
      canReorder,
      getIndexForPosition,
      insertItem,
      sortableItems.length,
      tryResolvePayload
    ]
  )

  const handleHtmlDragLeave = useCallback(() => {
    dragEnterCounter.current -= 1
    if (dragEnterCounter.current > 0) return
    dragEnterCounter.current = 0
    dropIndexRef.current = null
    setDropPreview(null)
    setIsDropHovered(false)
  }, [])

  useTouchDropZone<DroppedWidgetSpec>({
    dropZoneRef,
    getPayload: useCallback((ctx: unknown) => {
      const dragCtx = ctx as MarketplaceDragPayload | null
      if (dragCtx?.source !== 'marketplace') return null
      return {
        widgetKey: dragCtx.widgetKey,
        size: dragCtx.size ?? MARKETPLACE_CONTEXT_FALLBACK,
        ...(dragCtx.config?.publicId
          ? { config: { publicId: dragCtx.config.publicId } }
          : {})
      }
    }, []),
    onDragOver: useCallback(
      (
        payload: DroppedWidgetSpec,
        position: { clientX: number; clientY: number },
        dropZone: HTMLElement
      ) => updateDropPreview(payload, position.clientY, dropZone),
      [updateDropPreview]
    ),
    onDragLeave: useCallback(() => {
      dropIndexRef.current = null
      setDropPreview(null)
      setIsDropHovered(false)
    }, []),
    onDrop: useCallback(
      (
        payload: DroppedWidgetSpec,
        position: { clientX: number; clientY: number },
        dropZone: HTMLElement
      ) => {
        const index =
          dropIndexRef.current ??
          (canReorder
            ? getIndexForPosition(position.clientY, dropZone)
            : sortableItems.length)
        insertItem(index, payload)
        dropIndexRef.current = null
        setDropPreview(null)
        setIsDropHovered(false)
      },
      [canReorder, getIndexForPosition, insertItem, sortableItems.length]
    ),
    enabled: isTouchEnvironment
  })

  const preventContextMenu = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      event.stopPropagation()
    },
    []
  )

  const handleSortStart = useCallback(
    (event: DragStartEvent) => {
      const id = String(event.active.id)
      setActiveId(id)
      lastDragOverId.current = sortableItems.some(item => item.id === id)
        ? id
        : null
      setDropPreview(null)
      setIsDropHovered(false)
    },
    [sortableItems]
  )

  const handleSortOver = useCallback(
    (event: DragOverEvent) => {
      const overId = event.over?.id
      if (!overId) return
      const overIdStr = String(overId)
      if (sortableItems.some(item => item.id === overIdStr)) {
        lastDragOverId.current = overIdStr
      }
    },
    [sortableItems]
  )

  const handleSortEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      setActiveId(null)

      const activeIdStr = String(active.id)
      const fallbackId = over?.id ?? lastDragOverId.current
      const overIdStr = fallbackId != null ? String(fallbackId) : null

      lastDragOverId.current = null

      if (!overIdStr || overIdStr === activeIdStr) return

      editWidgets((current, canReorderNow) => {
        if (!canReorderNow) return null
        const forced = current.filter(item => item.isForced)
        const sortable = current.filter(item => !item.isForced)
        const oldIndex = sortable.findIndex(item => item.id === activeIdStr)
        const newIndex = sortable.findIndex(item => item.id === overIdStr)
        if (oldIndex === -1 || newIndex === -1) return null
        return {
          nextWidgets: normalizeDisplayOrder([
            ...forced,
            ...arrayMove(sortable, oldIndex, newIndex)
          ])
        }
      })
    },
    [editWidgets]
  )

  const handleSortCancel = useCallback(() => {
    setActiveId(null)
    lastDragOverId.current = null
  }, [])

  // scroll to top when widget enters full view, restore scroll when closed
  useEffect(() => {
    if (dropZoneRef.current) {
      if (fullViewWidgetId) {
        // save current scroll position and scroll to top
        savedScrollPosition.current = dropZoneRef.current.scrollTop
        dropZoneRef.current.scrollTop = 0
      } else {
        // restore scroll position when full view is closed
        dropZoneRef.current.scrollTop = savedScrollPosition.current
      }
    }
  }, [fullViewWidgetId])

  useEffect(() => {
    const renderedItems = [...forcedWidgets, ...sortableItems]
    if (
      fullViewWidgetId &&
      !renderedItems.some(
        item => item.id === fullViewWidgetId && !item.isCollapsed
      )
    ) {
      setFullViewWidgetId(null)
    }
  }, [fullViewWidgetId, forcedWidgets, sortableItems, setFullViewWidgetId])

  const handleOpenWidgetMarketplace = useOpenWindowMarketplace()

  const hasNoWidgets =
    forcedWidgets.length === 0 &&
    sortableItems.length === 0 &&
    !showDropBackground

  const matchesSearch = useCallback(
    (item: (typeof sortableItems)[number]) => {
      if (!searchQuery) return true
      const config = WIDGETS_CONFIG[item.widgetKey]
      const artifactTitle = isAiArtifactTile(item)
        ? (artifactTitles.get(item.config.publicId) ?? '')
        : ''
      return `${config?.name ?? ''} ${item.widgetKey} ${artifactTitle}`
        .toLocaleLowerCase()
        .includes(searchQuery)
    },
    [artifactTitles, searchQuery]
  )
  const visibleForcedWidgets = useMemo(
    () => forcedWidgets.filter(matchesSearch),
    [forcedWidgets, matchesSearch]
  )
  const visibleSortableItems = useMemo(
    () => sortableItems.filter(matchesSearch),
    [sortableItems, matchesSearch]
  )
  const widgetMotionInitial = suppressWidgetMotion
    ? false
    : { opacity: 0, scale: 1.2 }
  const widgetMotionExit = suppressWidgetMotion
    ? { opacity: 1, scale: 1, x: 0 }
    : { opacity: 0, x: -150 }
  const widgetMotionTransition = suppressWidgetMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 600, damping: 40 }

  useEffect(() => {
    const isFullViewWidgetVisible =
      visibleForcedWidgets.some(item => item.id === fullViewWidgetId) ||
      visibleSortableItems.some(item => item.id === fullViewWidgetId)
    if (fullViewWidgetId && !isFullViewWidgetVisible) {
      setFullViewWidgetId(null)
    }
  }, [
    fullViewWidgetId,
    setFullViewWidgetId,
    visibleForcedWidgets,
    visibleSortableItems
  ])

  if (isDesktopUtilityWindowOpen) return null

  return (
    <div ref={setSidebarWidgetsRef} className={styles.sidebarWidgets}>
      {(sortableItems.length > 0 || forcedWidgets.length > 0) && (
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <MagnifyerLineIcon
              className={styles.searchIcon}
              size={IconSizes.Size16}
              color='var(--white-t70)'
              aria-hidden='true'
            />
            <input
              aria-label={t('marketplace.searchLabel')}
              className={styles.searchInput}
              type='search'
              placeholder={t('marketplace.searchPlaceholder')}
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
            />
            {searchInput && (
              <button
                type='button'
                aria-label={t('marketplace.clearSearch')}
                className={styles.clearSearchButton}
                onClick={() => setSearchInput('')}
              >
                <MultiplyIcon
                  size={IconSizes.Size16}
                  color='var(--white-t70)'
                  aria-hidden='true'
                />
              </button>
            )}
          </div>
          {!showDropBackground &&
            (sortableItems.length > 0 || forcedWidgets.length > 0) && (
              <button
                className={styles.editWidgetsButton}
                onClick={() => handleOpenWidgetMarketplace()}
              >
                {t('edit_widgets')}
              </button>
            )}
        </div>
      )}
      <div
        className={cn(styles.content, {
          [styles.scrollLocked]: Boolean(fullViewWidgetId)
        })}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always
            }
          }}
          onDragStart={handleSortStart}
          onDragOver={handleSortOver}
          onDragEnd={handleSortEnd}
          onDragCancel={handleSortCancel}
        >
          <SortableContext
            items={sortableItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              ref={dropZoneRef}
              className={cn(styles.dropZone, {
                [styles.active]: isDropHovered,
                [styles.sorting]: Boolean(activeId),
                [styles.hasBackground]: showDropBackground,
                [styles.noWidgets]: hasNoWidgets
              })}
              onDragEnter={handleHtmlDragEnter}
              onDragOver={handleHtmlDragOver}
              onDrop={handleHtmlDrop}
              onDragLeave={handleHtmlDragLeave}
              onContextMenu={
                isTouchEnvironment ? preventContextMenu : undefined
              }
              role={hasNoWidgets ? 'button' : undefined}
              onClick={
                hasNoWidgets ? () => handleOpenWidgetMarketplace() : undefined
              }
            >
              {fullViewWidgetId && (
                <div
                  className={styles.fullViewOverlay}
                  onClick={() => toggleFullView(fullViewWidgetId)}
                />
              )}
              {/* forced widgets: not sortable, so no sortableId here */}
              <AnimatePresence>
                {visibleForcedWidgets.map(item => {
                  const config = WIDGETS_CONFIG[item.widgetKey]
                  if (!config || config.isHidden?.(user)) return null

                  const isInFullView = fullViewWidgetId === item.id
                  const { component: WidgetComponent, name } = config
                  const isCollapsed = item.isCollapsed ?? false

                  const sharedWidgetProps: Omit<
                    WidgetWrapperProps,
                    'children'
                  > = {
                    widget: item,
                    isDragging: false,
                    isActive: false,
                    isForced: true,
                    showRemoveButton: false,
                    widgetName: name,
                    supportedSizes: config.sizes,
                    isCollapsed,
                    isInFullView,
                    onToggleFullView: () => toggleFullView(item.id),
                    onResize: (size: WidgetSize) => {
                      if (size === item.size || !config.sizes.includes(size)) {
                        return
                      }
                      if (isInFullView) setFullViewWidgetId(null)
                      updateWidget(item.id, widget => ({
                        ...widget,
                        size
                      }))
                      logger.track('widget.resized', {
                        widgetKey: item.widgetKey,
                        fromSize: item.size,
                        toSize: size
                      })
                    },
                    onToggleCollapsed: () => {
                      if (isInFullView) setFullViewWidgetId(null)
                      updateWidget(item.id, widget => ({
                        ...widget,
                        isCollapsed: !isCollapsed
                      }))
                    }
                  }

                  return (
                    <motion.div
                      className={styles.widgetContainer}
                      key={item.id}
                      initial={widgetMotionInitial}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={widgetMotionExit}
                      transition={widgetMotionTransition}
                    >
                      <WidgetsErrorBoundary
                        isSidebarWidget
                        debugName={`Widget - ${name}`}
                        orientation={
                          item.size === WidgetSize.SMALL
                            ? 'horizontal'
                            : 'vertical'
                        }
                        widgetId={item.id}
                        {...sharedWidgetProps}
                      >
                        <LazyWidgetMount
                          height={
                            isCollapsed
                              ? WIDGET_COLLAPSED_HEIGHT
                              : WIDGET_NATURAL_HEIGHTS[item.size]
                          }
                        >
                          <WidgetComponent {...sharedWidgetProps} />
                        </LazyWidgetMount>
                      </WidgetsErrorBoundary>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
              <AnimatePresence>
                {visibleSortableItems.map(item => {
                  const index = sortableItems.findIndex(
                    sortableItem => sortableItem.id === item.id
                  )
                  const config = WIDGETS_CONFIG[item.widgetKey]
                  // hidden-with-auto-remove entries are dropped by the
                  // effect above on the next tick; skip rendering them so
                  // there's no flash of content in the meantime.
                  if (
                    config?.isHidden?.(user) &&
                    config?.hiddenAction === 'remove'
                  ) {
                    return null
                  }

                  const isInFullView = fullViewWidgetId === item.id
                  const targetIndex = dropPreview?.index ?? -1
                  const cssOrder =
                    targetIndex >= 0 && index >= targetIndex ? index + 1 : index

                  if (!config || config.isHidden?.(user)) {
                    return (
                      <motion.div
                        data-sidebar-widget='true'
                        data-sidebar-widget-index={index}
                        className={styles.widgetContainer}
                        key={item.id}
                        style={{ order: cssOrder }}
                        initial={widgetMotionInitial}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={widgetMotionExit}
                        transition={widgetMotionTransition}
                      >
                        <div
                          className={cn(styles.unavailable, {
                            [styles.unavailableS]:
                              item.size === WidgetSize.SMALL,
                            [styles.unavailableM]:
                              item.size === WidgetSize.MEDIUM,
                            [styles.unavailableL]:
                              item.size === WidgetSize.LARGE
                          })}
                          style={{ height: WIDGET_NATURAL_HEIGHTS[item.size] }}
                        >
                          <span className={styles.unavailableIcon}>⚠️</span>
                          <p className={styles.unavailableMessage}>
                            {t('unavailable_widget')}
                          </p>
                          <div className={styles.unavailableRemoveButton}>
                            <Button
                              label={t('remove_widget')}
                              icon={MultiplyIcon}
                              size={ButtonSizes.S}
                              variant={ButtonVariants.Ghost}
                              color={ButtonColors.Red}
                              data-testid='unavailable-widget-remove'
                              onClick={() => removeWidget(item.id)}
                            />
                          </div>
                        </div>
                      </motion.div>
                    )
                  }

                  const { component: WidgetComponent, name } = config
                  const isCollapsed = item.isCollapsed ?? false

                  const sharedWidgetProps: Omit<
                    WidgetWrapperProps,
                    'children'
                  > = {
                    widget: item,
                    isDragging: activeId === item.id,
                    sortableId: canReorder ? item.id : undefined,
                    isActive: activeId === item.id,
                    showRemoveButton: !!showRemoveWidgetButtons,
                    widgetName: name,
                    supportedSizes: config.sizes,
                    isCollapsed,
                    isInFullView,
                    onToggleFullView: () => toggleFullView(item.id),
                    onResize: size => {
                      if (size === item.size || !config.sizes.includes(size)) {
                        return
                      }
                      if (isInFullView) setFullViewWidgetId(null)
                      updateWidget(item.id, widget => ({ ...widget, size }))
                      logger.track('widget.resized', {
                        widgetKey: item.widgetKey,
                        fromSize: item.size,
                        toSize: size
                      })
                    },
                    onToggleCollapsed: () => {
                      if (isInFullView) setFullViewWidgetId(null)
                      updateWidget(
                        item.id,
                        widget => ({
                          ...widget,
                          isCollapsed: !isCollapsed
                        }),
                        {
                          onSuccess: () => {
                            logger.track(
                              isCollapsed
                                ? 'widget.expanded'
                                : 'widget.collapsed',
                              { widgetKey: item.widgetKey }
                            )
                          }
                        }
                      )
                    },
                    onRemove: () => removeWidget(item.id)
                  }

                  return (
                    <motion.div
                      data-sidebar-widget='true'
                      data-sidebar-widget-index={index}
                      className={styles.widgetContainer}
                      key={item.id}
                      style={{ order: cssOrder }}
                      initial={widgetMotionInitial}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={widgetMotionExit}
                      transition={widgetMotionTransition}
                    >
                      <WidgetsErrorBoundary
                        isSidebarWidget
                        debugName={`Widget - ${name}`}
                        orientation={
                          item.size === WidgetSize.SMALL
                            ? 'horizontal'
                            : 'vertical'
                        }
                        widgetId={item.id}
                        {...sharedWidgetProps}
                      >
                        <LazyWidgetMount
                          height={
                            isCollapsed
                              ? WIDGET_COLLAPSED_HEIGHT
                              : WIDGET_NATURAL_HEIGHTS[item.size]
                          }
                        >
                          <WidgetComponent {...sharedWidgetProps} />
                        </LazyWidgetMount>
                      </WidgetsErrorBoundary>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
              {searchQuery &&
                visibleForcedWidgets.length === 0 &&
                visibleSortableItems.length === 0 && (
                  <div className={styles.noResults}>
                    {t('marketplace.noResults')}
                  </div>
                )}
              {dropPreview && (
                <div
                  className={styles.dropPreview}
                  style={{
                    order: dropPreview.index,
                    height: WIDGET_NATURAL_HEIGHTS[dropPreview.size]
                  }}
                >
                  <span className={styles.dropPreviewLabel}>
                    {dropPreview.config?.publicId
                      ? (artifacts?.find(
                          a => a.public_id === dropPreview.config!.publicId
                        )?.title ?? WIDGETS_CONFIG[dropPreview.widgetKey].name)
                      : WIDGETS_CONFIG[dropPreview.widgetKey].name}{' '}
                    · {dropPreview.size.toUpperCase()}
                  </span>
                </div>
              )}
              {hasNoWidgets && (
                <div className={styles.noWidgetsText}>
                  <PlusCircleFullIcon color='var(--white)' />
                  <p>{t('add_widgets')}</p>
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  )
}
